import { Loading } from "gsintegrations";
import type React from "react";
import { createElement, lazy, Suspense } from "react";
import type { RouteObject } from "react-router-dom";
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from "react-router-dom";
import { AuthProvider } from "../auth/AuthContext";
import ProtectedRoute from "../auth/ProtectedRoute";
import PublicOnlyRoute from "../auth/PublicOnlyRoute";
import { SubscriptionProvider } from "../auth/SubscriptionContext";
import { EventEditErrorBoundary } from "../components/EventEditErrorBoundary";
import { RouteLoadError } from "../components/RouteLoadError";
import EventTerreiroOnlyRoute from "../components/EventTerreiroOnlyRoute";
import InstallPrompt from "../components/InstallPrompt";
import PasskeyEnrollmentGate from "../components/PasskeyEnrollmentGate";
import { AppBadgeSync } from "../components/AppBadgeSync";
import { ParticipationPushSync } from "../components/ParticipationPushSync";
import { PushNotificationEnrollment } from "../components/PushNotificationEnrollment";
import { PushNotificationPrompt } from "../components/PushNotificationPrompt";
import PermissionProtectedPage from "../components/PermissionProtectedPage";
import { WebSocketNotifications } from "../components/WebSocketNotifications/WebSocketNotifications";
import ProductAnalyticsOutlet from "../components/ProductAnalyticsOutlet";
import { ROUTE } from "../constants/routes";
import AttendanceInsightsRedirect from "../pages/AttendanceInsightsRedirect";
import LegacyFinancialNotificationRedirect from "./LegacyFinancialNotificationRedirect";
import { PERMISSION_ROUTE_MATRIX } from "./permissionRouteMatrix";
import LoginRedirect from "./LoginRedirect";
import { withRouteSuspense } from "./withRouteSuspense";
import FormChatAssistantProvider from "../context/FormChatAssistantProvider";
import OnboardingMedium from "../pages/OnboardingMedium";
import OnboardingTerreiro from "../pages/OnboardingTerreiro";

// Coming Soon page for production
const ComingSoon = lazy(() => import("../pages/ComingSoon"));

const appEnv = import.meta.env ?? {};

// Feature flag to enable/disable coming soon mode (disabled by default)
const COMING_SOON_MODE = appEnv.VITE_COMING_SOON_MODE === "true";

// Auth
const Signin = lazy(() => import("../pages/SignIn"));
const Signup = lazy(() => import("../pages/SignUp"));
const SignupTypeSelection = lazy(() => import("../pages/SignupTypeSelection"));
const GoogleOAuthResult = lazy(() => import("../pages/GoogleOAuthResult"));
const FacebookOAuthResult = lazy(() => import("../pages/FacebookOAuthResult"));
const ForgotPassword = lazy(() => import("../pages/ForgotPassword"));
const ResetPassword = lazy(() => import("../pages/ResetPassword"));
const QrLanding = lazy(() => import("../pages/QrLanding"));
const QrReturn = lazy(() => import("../pages/QrReturn"));
const InvitePage = lazy(() => import("../pages/InvitePage"));
const PublicTerreiroAgenda = lazy(
  () => import("../pages/PublicTerreiroAgenda")
);
const PublicEventTicket = lazy(() => import("../pages/PublicEventTicket"));
const TicketVoucher = lazy(() => import("../pages/TicketVoucher"));
const ProductUpdates = lazy(() => import("../pages/ProductUpdates"));
const ProductUpdateDetail = lazy(() => import("../pages/ProductUpdateDetail"));
const EventTicketingManagement = lazy(
  () => import("../pages/EventTicketingManagement")
);
const CreateIngresso = lazy(() => import("../pages/CreateIngresso"));
const EventsTicketsRedirect = lazy(
  () => import("../routes/EventsTicketsRedirect"),
);
const AttendanceCheckinEntry = lazy(
  () => import("../pages/AttendanceCheckinEntry")
);

// Onboardings
const OnboardingMain = lazy(() => import("../pages/OnboardingPage"));

// Main App Components
const Home = lazy(() => import("../pages/Home"));
const AttendanceInsights = lazy(() => import("../pages/AttendanceInsights"));
const MediumsLayout = lazy(() => import("../pages/MediumsLayout"));
const InventoryLayout = lazy(() => import("../pages/InventoryLayout"));
const InventoryPanel = lazy(() => import("../pages/InventoryPanel"));
const InventoryCategories = lazy(() => import("../pages/InventoryCategories"));
const SupplyPlanRedirect = lazy(() => import("../pages/SupplyPlanRedirect"));
const MediumsPanel = lazy(() => import("../pages/MediumsPanel"));
const MediumsRanks = lazy(() => import("../pages/MediumsRanks"));
const MediumsLines = lazy(() => import("../pages/MediumsLines"));
const SupplyPlan = lazy(() => import("../pages/SupplyPlan"));
const Notifications = lazy(() => import("../pages/Notifications"));

// Social network module
const SocialHome = lazy(() => import("../pages/social/SocialHome"));
const SocialOnboarding = lazy(() => import("../pages/social/SocialOnboarding"));
const CreateExperience = lazy(() => import("../pages/social/CreateExperience"));
const SocialExperienceDeepLink = lazy(
  () => import("../pages/social/SocialDeepLink")
);
const SocialQuestionDeepLink = lazy(() =>
  import("../pages/social/SocialDeepLink").then((m) => ({
    default: m.SocialQuestionDeepLink,
  }))
);
const SocialProfileDeepLink = lazy(() =>
  import("../pages/social/SocialDeepLink").then((m) => ({
    default: m.SocialProfileDeepLink,
  }))
);
const Questions = lazy(() => import("../pages/social/Questions"));
const CreateQuestion = lazy(() => import("../pages/social/CreateQuestion"));
const Similar = lazy(() => import("../pages/social/Similar"));
const JourneyProfile = lazy(() => import("../pages/social/JourneyProfile"));
const EditJourney = lazy(() => import("../pages/social/EditJourney"));
const SocialDiscover = lazy(() => import("../pages/social/SocialDiscover"));
const SocialSaved = lazy(() => import("../pages/social/SocialSaved"));
const SocialConnections = lazy(
  () => import("../pages/social/SocialConnections")
);
const SocialNotifications = lazy(
  () => import("../pages/social/SocialNotifications")
);
const SocialPrivacy = lazy(() => import("../pages/social/SocialPrivacy"));
const SocialMessages = lazy(() => import("../pages/social/SocialMessages"));
const SocialConversation = lazy(
  () => import("../pages/social/SocialConversation")
);

// Events module
const Events = lazy(() => import("../pages/Events"));
const EventView = lazy(() => import("../pages/EventView"));
const CreateEvent = lazy(() => import("../pages/CreateEvent"));
const EditEvent = lazy(() => import("../pages/EditEvent"));
const CreateFromTemplateEvent = lazy(
  () => import("../pages/CreateFromTemplateEvent")
);

// Credentials Management module
const CredentialsManagement = lazy(
  () => import("../pages/CredentialsManagement")
);
const PasskeysManagement = lazy(() => import("../pages/PasskeysManagement"));

// Activities module
const Activities = lazy(() => import("../pages/Activities"));
const CreateActivity = lazy(() => import("../pages/CreateActivity"));
const ActivityView = lazy(() => import("../pages/ActivityView"));
const EditActivity = lazy(() => import("../pages/EditActivity"));
const CreateFromModelActivity = lazy(
  () => import("../pages/CreateFromModelActivity")
);

// Mediums module
const Mediums = lazy(() => import("../pages/Mediums"));
const MediumManagement = lazy(() => import("../pages/MediumManagement"));
const CreateMedium = lazy(() => import("../pages/CreateMedium"));
const Profile = lazy(() => import("../pages/Profile"));
const ProfileManagement = lazy(() => import("../pages/ProfileManagement"));
const SpiritualProfile = lazy(() => import("../pages/SpiritualProfile"));
const CreateSpiritualProfile = lazy(
  () => import("../pages/CreateSpiritualProfile")
);
const MediumHub = lazy(() => import("../pages/MediumHub"));
const ProfileMediumRedirect = lazy(
  () => import("../pages/ProfileMediumRedirect")
);
const ContactsManagement = lazy(() => import("../pages/ContactsManagement"));
const MediumProfileView = lazy(() => import("../pages/MediumProfileView"));

// Address Management module
const AddressManagement = lazy(() => import("../pages/AddressManagement"));

// Inventory module
const Inventory = lazy(() => import("../pages/Inventory"));
const InventoryView = lazy(() => import("../pages/InventoryView"));
const CreateInventory = lazy(() => import("../pages/CreateInventory"));
const EditInventory = lazy(() => import("../pages/EditInventory"));

// Terreiro module
const Terreiro = lazy(() => import("../pages/Terreiro"));
const TerreiroSearch = lazy(() => import("../pages/TerreiroSearch"));
const JoinTerreiro = lazy(() => import("../pages/JoinTerreiro"));
const TerreiroProfileManagement = lazy(
  () => import("../pages/TerreiroProfileManagement")
);

// Financial module
const FinancialAccounts = lazy(() => import("../pages/FinancialAccounts"));
const FinancialAccountDirectory = lazy(
  () => import("../pages/FinancialAccountDirectory")
);
const FinancialMonthlyFeeHub = lazy(
  () => import("../pages/FinancialMonthlyFeeHub")
);
const FinancialLayout = lazy(() => import("../pages/FinancialLayout"));
const CreateFinancialAccount = lazy(
  () => import("../pages/CreateFinancialAccount")
);
const EditFinancialAccount = lazy(
  () => import("../pages/EditFinancialAccount")
);
const CreateTransaction = lazy(() => import("../pages/CreateTransaction"));
const MonthlyFeeManagement = lazy(
  () => import("../pages/MonthlyFeeManagement")
);

// Income and Outcome Lists
const IncomeList = lazy(() => import("../pages/IncomeList"));
const OutcomeList = lazy(() => import("../pages/OutcomeList"));

// Income and Outcome Edit
const EditIncome = lazy(() => import("../pages/EditIncome"));
const EditOutcome = lazy(() => import("../pages/EditOutcome"));

// Financial Transparency (for mediums)
const FinancialTransparency = lazy(
  () => import("../pages/FinancialTransparency")
);

// Settings
const Settings = lazy(() => import("../pages/Settings"));
const SettingsMeetings = lazy(() => import("../pages/SettingsMeetings"));
const SettingsUsageQuota = lazy(() => import("../pages/UsageQuota"));
const SettingsPlanManagement = lazy(() => import("../pages/PlanManagement"));
const SettingsNotificationSettings = lazy(
  () => import("../pages/NotificationSettings")
);
const SettingsTerreiroPolicy = lazy(() => import("../pages/TerreiroPolicy"));
const AffiliateProgram = lazy(() => import("../pages/AffiliateProgram"));
const RoleManagement = lazy(() => import("../pages/RoleManagement"));
const RoleCreate = lazy(() => import("../pages/RoleCreate"));
const RoleEdit = lazy(() => import("../pages/RoleEdit"));
const MyPermissions = lazy(() => import("../pages/MyPermissions"));
const SubscriptionRequiredForMedium = lazy(
  () => import("../pages/SubscriptionRequiredForMedium")
);

// Not Found
const NotFound = lazy(() => import("../pages/NotFound"));

const SubscriptionCancelation = lazy(
  () => import("../pages/SubscriptionCancelation")
);
const SubscriptionSuccessFull = lazy(
  () => import("../pages/SubscriptionSuccessFull")
);

// Examples & Demo
const MoonDataExample = lazy(() => import("../pages/MoonDataExample"));

// Ritual Baths module
const RitualBaths = lazy(() => import("../pages/RitualBaths"));
const RitualBathView = lazy(() => import("../pages/RitualBathView"));
const CreateRitualBath = lazy(() => import("../pages/CreateRitualBath"));
const EditRitualBath = lazy(() => import("../pages/EditRitualBath"));
const ScheduleRitualBath = lazy(() => import("../pages/ScheduleRitualBath"));
const RitualBathCategories = lazy(
  () => import("../pages/RitualBathCategories")
);

// Gamification module
const Leaderboard = lazy(() => import("../pages/Leaderboard"));
const Badges = lazy(() => import("../pages/Badges"));
const GamificationHelp = lazy(() => import("../pages/GamificationHelp"));
const Academy = lazy(() => import("../pages/Academy"));
const AcademyModule = lazy(() => import("../pages/AcademyModule"));

// Development only - Subscription testing page
const SubscriptionTestPage = appEnv.DEV
  ? lazy(() => import("../pages/SubscriptionTestPage"))
  : null;

// Admin
//const Admin = lazy(() => import("../pages/Admin"));

const wrapWithPermission = (routeKey: string, children: React.ReactNode) => {
  const rule = PERMISSION_ROUTE_MATRIX[routeKey];

  if (!rule) {
    return children;
  }

  return (
    <PermissionProtectedPage
      requiredPermissions={rule.permissions}
      mode={rule.mode}
      allowWithoutTerreiro={rule.allowWithoutTerreiro}
      allowWaitingApproval={rule.allowWaitingApproval}
      requiredFeature={rule.requiredFeature}
    >
      {children}
    </PermissionProtectedPage>
  );
};

const wrapEventTerreiroOnly = (children: React.ReactNode) => {
  return <EventTerreiroOnlyRoute>{children}</EventTerreiroOnlyRoute>;
};

export const ProvidersLayout: React.FC = () => (
  <SubscriptionProvider>
    <WebSocketNotifications enabled>
      <ProvidersLayoutContent />
    </WebSocketNotifications>
  </SubscriptionProvider>
);

export const ProvidersLayoutContent: React.FC = () => (
  <>
    <Outlet />
    <FormChatAssistantProvider />
    <PushNotificationEnrollment />
    <PushNotificationPrompt />
    <AppBadgeSync />
    <ParticipationPushSync />
    <InstallPrompt />
    <PasskeyEnrollmentGate />
  </>
);

const routes: RouteObject[] = [
  {
    path: ROUTE.APP_HOME,
    element: <ProvidersLayout />,
    errorElement: createElement(RouteLoadError),
    children: [
      {
        element: <ProductAnalyticsOutlet />,
        children: [
          {
            element: <PublicOnlyRoute />,
            children: [
              { path: ROUTE.SIGNIN, element: createElement(Signin) },
              { path: ROUTE.SIGNUP, element: createElement(Signup) },
              {
                path: ROUTE.FORGOT_PASSWORD,
                element: createElement(ForgotPassword),
              },
              {
                path: ROUTE.RESET_PASSWORD,
                element: createElement(ResetPassword),
              },
            ],
          },
          { path: ROUTE.LOGIN, element: <LoginRedirect /> },
          {
            path: ROUTE.OAUTH_GOOGLE_RESULT,
            element: createElement(GoogleOAuthResult),
          },
          {
            path: ROUTE.OAUTH_FACEBOOK_RESULT,
            element: createElement(FacebookOAuthResult),
          },
          {
            path: ROUTE.SIGNUP_TYPE,
            element: createElement(SignupTypeSelection),
          },
          // Onboarding routes — accessible in ANY auth state.
          // Users arrive here unauthenticated (from signup) and become authenticated
          // mid-flow when the signup API sets cookies. Placing these inside
          // PublicOnlyRoute would redirect the user to HOME as soon as auth succeeds,
          // interrupting the onboarding form.
          {
            path: ROUTE.ONBOARDING_MEDIUM,
            element: createElement(OnboardingMedium),
          },
          {
            path: ROUTE.ONBOARDING_TERREIRO,
            element: createElement(OnboardingTerreiro),
          },
          {
            path: ROUTE.QR,
            element: createElement(QrLanding),
          },
          {
            path: ROUTE.QR_RETURN,
            element: createElement(QrReturn),
          },
          {
            path: ROUTE.INVITE,
            element: createElement(InvitePage),
          },
          {
            path: ROUTE.PUBLIC_TERREIRO,
            element: createElement(PublicTerreiroAgenda),
          },
          {
            path: ROUTE.PUBLIC_EVENT,
            element: createElement(PublicEventTicket),
          },
          {
            path: ROUTE.TICKET_VOUCHER,
            element: createElement(TicketVoucher),
          },
          {
            path: ROUTE.UPDATES,
            element: createElement(ProductUpdates),
          },
          {
            path: ROUTE.UPDATE_DETAIL,
            element: createElement(ProductUpdateDetail),
          },
          {
            path: ROUTE.ATTENDANCE_CHECKIN_ENTRY,
            element: createElement(AttendanceCheckinEntry),
          },
          {
            path: ROUTE.SOCIAL_EXPERIENCE,
            element: createElement(SocialExperienceDeepLink),
          },
          {
            path: ROUTE.SOCIAL_QUESTION,
            element: createElement(SocialQuestionDeepLink),
          },
          {
            path: ROUTE.SOCIAL_PROFILE,
            element: createElement(SocialProfileDeepLink),
          },
          {
            element: <ProtectedRoute />,
            children: [
              { index: true, element: createElement(Home) },
              {
                path: ROUTE.ATTENDANCE_INSIGHTS,
                element: wrapWithPermission(
                  ROUTE.ATTENDANCE_INSIGHTS,
                  <AttendanceInsightsRedirect />
                ),
              },
              {
                path: ROUTE.SUPPLY_PLAN,
                element: createElement(SupplyPlanRedirect),
              },
              {
                path: ROUTE.NOTIFICATIONS,
                element: createElement(Notifications),
              },

              // Social network (authenticated)
              { path: ROUTE.SOCIAL, element: createElement(SocialHome) },
              {
                path: ROUTE.SOCIAL_ONBOARDING,
                element: createElement(SocialOnboarding),
              },
              {
                path: ROUTE.SOCIAL_EXPERIENCES_NEW,
                element: createElement(CreateExperience),
              },
              {
                path: ROUTE.SOCIAL_QUESTIONS,
                element: createElement(Questions),
              },
              {
                path: ROUTE.SOCIAL_QUESTION_NEW,
                element: createElement(CreateQuestion),
              },
              { path: ROUTE.SOCIAL_SIMILAR, element: createElement(Similar) },
              {
                path: ROUTE.SOCIAL_PROFILE_ME,
                element: createElement(JourneyProfile),
              },
              {
                path: ROUTE.SOCIAL_PROFILE_EDIT,
                element: createElement(EditJourney),
              },
              {
                path: ROUTE.SOCIAL_DISCOVER,
                element: createElement(SocialDiscover),
              },
              {
                path: ROUTE.SOCIAL_SAVED,
                element: createElement(SocialSaved),
              },
              {
                path: ROUTE.SOCIAL_CONNECTIONS,
                element: createElement(SocialConnections),
              },
              {
                path: ROUTE.SOCIAL_CONNECTIONS_USER,
                element: createElement(SocialConnections),
              },
              {
                path: ROUTE.SOCIAL_NOTIFICATIONS,
                element: createElement(SocialNotifications),
              },
              {
                path: ROUTE.SOCIAL_PRIVACY,
                element: createElement(SocialPrivacy),
              },
              {
                path: ROUTE.SOCIAL_MESSAGES,
                element: createElement(SocialMessages),
              },
              {
                path: ROUTE.SOCIAL_CONVERSATION,
                element: createElement(SocialConversation),
              },

              // Onboarding main page (authenticated users; steps tailored by permissions)
              {
                path: ROUTE.ONBOARDING,
                element: createElement(OnboardingMain),
              },
              // Events module (example: accessible to both admin and medium)
              {
                path: ROUTE.EVENTS,
                element: wrapEventTerreiroOnly(
                  wrapWithPermission(ROUTE.EVENTS, <Events />)
                ),
              },
              {
                path: ROUTE.EVENTS_NEW,
                element: wrapEventTerreiroOnly(
                  wrapWithPermission(ROUTE.EVENTS_NEW, <CreateEvent />)
                ),
              },
              {
                path: "/events/tickets",
                element: createElement(EventsTicketsRedirect),
              },
              {
                path: ROUTE.EVENT,
                element: wrapEventTerreiroOnly(createElement(EventView)),
              },
              {
                path: ROUTE.EVENT_EDIT,
                element: wrapEventTerreiroOnly(createElement(EditEvent)),
                errorElement: createElement(EventEditErrorBoundary),
              },
              {
                path: ROUTE.EVENT_TICKETS,
                element: wrapEventTerreiroOnly(
                  wrapWithPermission(
                    ROUTE.EVENT_TICKETS,
                    <EventTicketingManagement />
                  )
                ),
              },
              {
                path: ROUTE.INGRESSOS_NEW,
                element: wrapEventTerreiroOnly(
                  wrapWithPermission(
                    ROUTE.INGRESSOS_NEW,
                    withRouteSuspense(<CreateIngresso />)
                  )
                ),
              },
              {
                path: "/events/template/:id/create",
                element: wrapEventTerreiroOnly(
                  createElement(CreateFromTemplateEvent)
                ),
              },

              // Activities module (example: accessible to both admin and medium)
              {
                path: ROUTE.ACTIVITIES,
                element: wrapWithPermission(ROUTE.ACTIVITIES, <Activities />),
              },
              {
                path: ROUTE.ACTIVITIES_NEW,
                element: wrapWithPermission(
                  ROUTE.ACTIVITIES_NEW,
                  <CreateActivity />
                ),
              },
              { path: ROUTE.ACTIVITY, element: createElement(ActivityView) },
              {
                path: ROUTE.ACTIVITY_EDIT,
                element: wrapWithPermission(
                  ROUTE.ACTIVITY_EDIT,
                  <EditActivity />
                ),
              },
              {
                path: ROUTE.ACTIVITY_USE_MODEL,
                element: wrapWithPermission(
                  ROUTE.ACTIVITY_USE_MODEL,
                  <CreateFromModelActivity />
                ),
              },

              // Rituals: same pages as activities; the type comes from the /rituals path.
              // Keys force a remount when switching between /activities and /rituals.
              {
                path: ROUTE.RITUALS,
                element: wrapWithPermission(
                  ROUTE.RITUALS,
                  <Activities key="rituals" />
                ),
              },
              {
                path: ROUTE.RITUALS_NEW,
                element: wrapWithPermission(
                  ROUTE.RITUALS_NEW,
                  <CreateActivity key="rituals-new" />
                ),
              },
              {
                path: ROUTE.RITUAL,
                element: createElement(ActivityView, { key: "ritual" }),
              },
              {
                path: ROUTE.RITUAL_EDIT,
                element: wrapWithPermission(
                  ROUTE.RITUAL_EDIT,
                  <EditActivity key="ritual-edit" />
                ),
              },
              {
                path: ROUTE.RITUAL_USE_MODEL,
                element: wrapWithPermission(
                  ROUTE.RITUAL_USE_MODEL,
                  <CreateFromModelActivity key="ritual-use-model" />
                ),
              },

              // Profile/Medium module
              {
                path: ROUTE.PROFILE,
                element: wrapWithPermission(ROUTE.PROFILE, <Profile />),
              },
              {
                path: ROUTE.PROFILE_DATA,
                element: wrapWithPermission(
                  ROUTE.PROFILE_DATA,
                  <ProfileManagement />
                ),
              },
              {
                path: ROUTE.PROFILE_MEDIUM_ROOT,
                element: createElement(ProfileMediumRedirect),
              },
              {
                path: ROUTE.PROFILE_MEDIUM,
                element: wrapWithPermission(
                  ROUTE.PROFILE_MEDIUM,
                  <MediumHub />
                ),
              },
              {
                path: ROUTE.PROFILE_MEDIUM_DATA,
                element: wrapWithPermission(
                  ROUTE.PROFILE_MEDIUM_DATA,
                  <MediumManagement />
                ),
              },
              {
                path: ROUTE.PROFILE_ADDRESS,
                element: wrapWithPermission(
                  ROUTE.PROFILE_ADDRESS,
                  <AddressManagement />
                ),
              },
              {
                path: ROUTE.PROFILE_ADDRESS_WITH_ID,
                element: wrapWithPermission(
                  ROUTE.PROFILE_ADDRESS_WITH_ID,
                  <AddressManagement />
                ),
              },
              {
                path: ROUTE.PROFILE_CONTACTS,
                element: wrapWithPermission(
                  ROUTE.PROFILE_CONTACTS,
                  <ContactsManagement />
                ),
              },
              {
                path: ROUTE.PROFILE_CREDENTIALS,
                element: wrapWithPermission(
                  ROUTE.PROFILE_CREDENTIALS,
                  <CredentialsManagement />
                ),
              },
              {
                path: ROUTE.PROFILE_PASSKEYS,
                element: wrapWithPermission(
                  ROUTE.PROFILE_PASSKEYS,
                  <PasskeysManagement />
                ),
              },
              {
                path: ROUTE.SPIRITUAL_PROFILE_NEW,
                element: wrapWithPermission(
                  ROUTE.SPIRITUAL_PROFILE_NEW,
                  <CreateSpiritualProfile />
                ),
              },
              {
                path: ROUTE.SPIRITUAL_PROFILE,
                element: wrapWithPermission(
                  ROUTE.SPIRITUAL_PROFILE,
                  <SpiritualProfile />
                ),
              },

              // Mediums module
              {
                element: createElement(MediumsLayout),
                children: [
                  {
                    path: ROUTE.MEDIUMS,
                    element: wrapWithPermission(
                      ROUTE.MEDIUMS,
                      <MediumsPanel />
                    ),
                  },
                  {
                    path: ROUTE.MEDIUMS_LIST,
                    element: wrapWithPermission(ROUTE.MEDIUMS_LIST, <Mediums />),
                  },
                  {
                    path: ROUTE.MEDIUMS_ATTENDANCE,
                    element: wrapWithPermission(
                      ROUTE.MEDIUMS_ATTENDANCE,
                      <AttendanceInsights />
                    ),
                  },
                  {
                    path: ROUTE.MEDIUMS_RANKS,
                    element: wrapWithPermission(
                      ROUTE.MEDIUMS_RANKS,
                      <MediumsRanks />
                    ),
                  },
                  {
                    path: ROUTE.MEDIUMS_LINES,
                    element: wrapWithPermission(
                      ROUTE.MEDIUMS_LINES,
                      <MediumsLines />
                    ),
                  },
                  {
                    path: ROUTE.MEDIUMS_NEW,
                    element: wrapWithPermission(
                      ROUTE.MEDIUMS_NEW,
                      <CreateMedium />
                    ),
                  },
                  {
                    path: ROUTE.MEDIUM,
                    element: wrapWithPermission(ROUTE.MEDIUM, <Profile />),
                  },
                ],
              },

              {
                element: createElement(InventoryLayout),
                children: [
                  {
                    path: ROUTE.INVENTORY,
                    element: wrapWithPermission(
                      ROUTE.INVENTORY,
                      <InventoryPanel />,
                    ),
                  },
                  {
                    path: ROUTE.INVENTORY_LIST,
                    element: wrapWithPermission(ROUTE.INVENTORY_LIST, <Inventory />),
                  },
                  {
                    path: ROUTE.INVENTORY_SUPPLY,
                    element: wrapWithPermission(
                      ROUTE.INVENTORY_SUPPLY,
                      <SupplyPlan />,
                    ),
                  },
                  {
                    path: ROUTE.INVENTORY_CATEGORIES,
                    element: wrapWithPermission(
                      ROUTE.INVENTORY_CATEGORIES,
                      <InventoryCategories />,
                    ),
                  },
                  {
                    path: ROUTE.INVENTORY_NEW,
                    element: wrapWithPermission(
                      ROUTE.INVENTORY_NEW,
                      <CreateInventory />,
                    ),
                  },
                  {
                    path: ROUTE.INVENTORY_EDIT,
                    element: wrapWithPermission(
                      ROUTE.INVENTORY_EDIT,
                      <EditInventory />,
                    ),
                  },
                  {
                    path: ROUTE.INVENTORY_VIEW,
                    element: wrapWithPermission(
                      ROUTE.INVENTORY_VIEW,
                      <InventoryView />,
                    ),
                  },
                ],
              },

              // Terreiro module
              {
                path: ROUTE.TERREIRO,
                element: wrapWithPermission(ROUTE.TERREIRO, <Terreiro />),
              },
              {
                path: ROUTE.TERREIRO_SEARCH,
                element: wrapWithPermission(
                  ROUTE.TERREIRO_SEARCH,
                  <TerreiroSearch />
                ),
              },
              {
                path: ROUTE.TERREIRO_JOIN,
                element: wrapWithPermission(
                  ROUTE.TERREIRO_JOIN,
                  <JoinTerreiro />
                ),
              },
              {
                path: ROUTE.TERREIRO_PROFILE,
                element: wrapWithPermission(
                  ROUTE.TERREIRO_PROFILE,
                  <TerreiroProfileManagement />
                ),
              },
              {
                path: ROUTE.TERREIRO_ADDRESS,
                element: wrapWithPermission(
                  ROUTE.TERREIRO_ADDRESS,
                  <AddressManagement />
                ),
              },
              {
                path: ROUTE.TERREIRO_CONTACT,
                element: wrapWithPermission(
                  ROUTE.TERREIRO_CONTACT,
                  <ContactsManagement />
                ),
              },
              {
                path: ROUTE.TERREIRO_MEDIUM_VIEW,
                element: wrapWithPermission(
                  ROUTE.TERREIRO_MEDIUM_VIEW,
                  <MediumProfileView />
                ),
              },

              // Financial module
              {
                element: createElement(FinancialLayout),
                children: [
                  {
                    path: ROUTE.FINANCIAL,
                    element: wrapWithPermission(
                      ROUTE.FINANCIAL,
                      <FinancialAccounts />
                    ),
                  },
                  {
                    path: ROUTE.FINANCIAL_ACCOUNTS,
                    element: wrapWithPermission(
                      ROUTE.FINANCIAL_ACCOUNTS,
                      <FinancialAccountDirectory />
                    ),
                  },
                  {
                    path: ROUTE.FINANCIAL_NEW,
                    element: wrapWithPermission(
                      ROUTE.FINANCIAL_NEW,
                      <CreateFinancialAccount />
                    ),
                  },
                  {
                    path: ROUTE.FINANCIAL_ACCOUNT,
                    element: wrapWithPermission(
                      ROUTE.FINANCIAL_ACCOUNT,
                      <EditFinancialAccount />
                    ),
                  },
                  {
                    path: ROUTE.FINANCIAL_MONTHLY_FEE,
                    element: wrapWithPermission(
                      ROUTE.FINANCIAL_MONTHLY_FEE,
                      <FinancialMonthlyFeeHub />
                    ),
                  },
                  {
                    path: ROUTE.FINANCIAL_MONTHLY_FEE_EDIT,
                    element: wrapWithPermission(
                      ROUTE.FINANCIAL_MONTHLY_FEE_EDIT,
                      <MonthlyFeeManagement />
                    ),
                  },
                  {
                    path: ROUTE.FINANCIAL_MONTHLY_FEE_PAYMENTS,
                    element: wrapWithPermission(
                      ROUTE.FINANCIAL_MONTHLY_FEE_PAYMENTS,
                      <Navigate to={ROUTE.FINANCIAL_MONTHLY_FEE} replace />,
                    ),
                  },
                  {
                    path: ROUTE.FINANCIAL_INCOME,
                    element: wrapWithPermission(
                      ROUTE.FINANCIAL_INCOME,
                      <IncomeList />
                    ),
                  },
                  {
                    path: ROUTE.FINANCIAL_OUTCOME,
                    element: wrapWithPermission(
                      ROUTE.FINANCIAL_OUTCOME,
                      <OutcomeList />
                    ),
                  },
                  {
                    path: ROUTE.FINANCIAL_INCOME_EDIT,
                    element: wrapWithPermission(
                      ROUTE.FINANCIAL_INCOME_EDIT,
                      <EditIncome />
                    ),
                  },
                  {
                    path: ROUTE.FINANCIAL_OUTCOME_EDIT,
                    element: wrapWithPermission(
                      ROUTE.FINANCIAL_OUTCOME_EDIT,
                      <EditOutcome />
                    ),
                  },
                  {
                    path: "/financial/incoming/:id",
                    element: (
                      <LegacyFinancialNotificationRedirect kind="income" />
                    ),
                  },
                  {
                    path: "/financial/outcome/:id",
                    element: (
                      <LegacyFinancialNotificationRedirect kind="outcome" />
                    ),
                  },
                  {
                    path: ROUTE.FINANCIAL_TRANSPARENCY,
                    element: wrapWithPermission(
                      ROUTE.FINANCIAL_TRANSPARENCY,
                      <FinancialTransparency />
                    ),
                  },
                  {
                    path: ROUTE.TRANSACTIONS_NEW,
                    element: wrapWithPermission(
                      ROUTE.TRANSACTIONS_NEW,
                      <CreateTransaction />
                    ),
                  },
                ],
              },

              // Settings
              {
                path: ROUTE.SETTINGS,
                element: wrapWithPermission(ROUTE.SETTINGS, <Settings />),
              },
              {
                path: ROUTE.SETTINGS_MEETINGS,
                element: wrapWithPermission(
                  ROUTE.SETTINGS_MEETINGS,
                  <SettingsMeetings />
                ),
              },
              {
                path: ROUTE.SETTINGS_USAGE,
                element: wrapWithPermission(
                  ROUTE.SETTINGS_USAGE,
                  <SettingsUsageQuota />
                ),
              },
              {
                path: ROUTE.SETTINGS_PLAN,
                element: wrapWithPermission(
                  ROUTE.SETTINGS_PLAN,
                  <SettingsPlanManagement />
                ),
              },
              {
                path: ROUTE.SETTINGS_NOTIFICATIONS,
                element: wrapWithPermission(
                  ROUTE.SETTINGS_NOTIFICATIONS,
                  <SettingsNotificationSettings />
                ),
              },
              {
                path: ROUTE.SETTINGS_PRIVACY_POLICY,
                element: wrapWithPermission(
                  ROUTE.SETTINGS_PRIVACY_POLICY,
                  <SettingsTerreiroPolicy />
                ),
              },
              {
                path: ROUTE.AFFILIATE_PROGRAM,
                element: wrapWithPermission(
                  ROUTE.AFFILIATE_PROGRAM,
                  <AffiliateProgram />
                ),
              },

              // Role Management (Admin only)
              {
                path: ROUTE.ROLE_MANAGEMENT,
                element: wrapWithPermission(
                  ROUTE.ROLE_MANAGEMENT,
                  <RoleManagement />
                ),
              },
              {
                path: ROUTE.ROLE_CREATE,
                element: wrapWithPermission(ROUTE.ROLE_CREATE, <RoleCreate />),
              },
              {
                path: ROUTE.ROLE_EDIT,
                element: wrapWithPermission(ROUTE.ROLE_EDIT, <RoleEdit />),
              },

              // My Permissions (Any authenticated user)
              {
                path: ROUTE.MY_PERMISSIONS,
                element: <MyPermissions />,
              },

              // Subscription required info for medium users
              {
                path: ROUTE.SUBSCRIPTION_REQUIRED,
                element: wrapWithPermission(
                  ROUTE.SUBSCRIPTION_REQUIRED,
                  <SubscriptionRequiredForMedium />
                ),
              },

              // Subscriptions
              {
                path: ROUTE.SUBSCRIPTION_SUCCESSFUL,
                element: createElement(SubscriptionSuccessFull),
              },
              {
                path: ROUTE.SUBSCRIPTION_CANCELATION,
                element: createElement(SubscriptionCancelation),
              },

              // Ritual Baths module
              {
                path: ROUTE.RITUAL_BATHS,
                element: wrapWithPermission(
                  ROUTE.RITUAL_BATHS,
                  <RitualBaths />
                ),
              },
              {
                path: ROUTE.RITUAL_BATHS_NEW,
                element: wrapWithPermission(
                  ROUTE.RITUAL_BATHS_NEW,
                  <CreateRitualBath />
                ),
              },
              {
                path: ROUTE.RITUAL_BATH_SCHEDULE,
                element: wrapWithPermission(
                  ROUTE.RITUAL_BATH_SCHEDULE,
                  <ScheduleRitualBath />
                ),
              },
              {
                path: ROUTE.RITUAL_BATH,
                element: wrapWithPermission(
                  ROUTE.RITUAL_BATH,
                  <RitualBathView />
                ),
              },
              {
                path: ROUTE.RITUAL_BATH_EDIT,
                element: wrapWithPermission(
                  ROUTE.RITUAL_BATH_EDIT,
                  <EditRitualBath />
                ),
              },
              {
                path: ROUTE.RITUAL_BATH_CATEGORIES,
                element: wrapWithPermission(
                  ROUTE.RITUAL_BATH_CATEGORIES,
                  <RitualBathCategories />
                ),
              },

              // Gamification pages
              {
                path: ROUTE.LEADERBOARD,
                element: wrapWithPermission(ROUTE.LEADERBOARD, <Leaderboard />),
              },
              {
                path: ROUTE.BADGES,
                element: wrapWithPermission(ROUTE.BADGES, <Badges />),
              },
              {
                path: ROUTE.GAMIFICATION_HELP,
                element: wrapWithPermission(
                  ROUTE.GAMIFICATION_HELP,
                  <GamificationHelp />
                ),
              },
              {
                path: ROUTE.ACADEMY,
                element: wrapWithPermission(ROUTE.ACADEMY, <Academy />),
              },
              {
                path: ROUTE.ACADEMY_MODULE,
                element: wrapWithPermission(
                  ROUTE.ACADEMY_MODULE,
                  <AcademyModule />
                ),
              },

              // Examples & Demo pages
              {
                path: ROUTE.MOON_EXAMPLE,
                element: createElement(MoonDataExample),
              },
              // Development only - Subscription testing page
              ...(appEnv.DEV && SubscriptionTestPage
                ? [
                    {
                      path: "/dev/subscription-test",
                      element: wrapWithPermission(
                        "/dev/subscription-test",
                        <SubscriptionTestPage />
                      ),
                    },
                  ]
                : []),
            ],
          },
          { path: ROUTE.NOT_FOUND, element: createElement(NotFound) },
        ],
      },
    ],
  },
];

// Coming soon routes - catch all paths in production
const comingSoonRoutes: RouteObject[] = [
  {
    element: <ProductAnalyticsOutlet />,
    errorElement: createElement(RouteLoadError),
    children: [
      {
        path: ROUTE.QR,
        element: createElement(QrLanding),
      },
      {
        path: ROUTE.QR_RETURN,
        element: createElement(QrReturn),
      },
      {
        path: ROUTE.INVITE,
        element: createElement(InvitePage),
      },
      {
        path: ROUTE.PUBLIC_TERREIRO,
        element: createElement(PublicTerreiroAgenda),
      },
      {
        path: ROUTE.PUBLIC_EVENT,
        element: createElement(PublicEventTicket),
      },
      {
        path: ROUTE.TICKET_VOUCHER,
        element: createElement(TicketVoucher),
      },
      {
        path: ROUTE.UPDATES,
        element: createElement(ProductUpdates),
      },
      {
        path: ROUTE.UPDATE_DETAIL,
        element: createElement(ProductUpdateDetail),
      },
      {
        path: "*",
        element: createElement(ComingSoon),
      },
    ],
  },
];

export const AppRouter: React.FC = () => {
  const router = createBrowserRouter(
    COMING_SOON_MODE ? comingSoonRoutes : routes
  );
  return (
    <AuthProvider>
      <Suspense fallback={<Loading />}>
        <RouterProvider router={router} />
      </Suspense>
    </AuthProvider>
  );
};
